import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

type Props = {
  title: string;
  subtitle: string;
  to: string;
  headingLevel: 'h2' | 'h3';
};

/**
 * The card's title and origin line — and its only link.
 *
 * The link's `::after` is stretched over the whole card, so anywhere that is
 * not one of the overlay controls opens the car. That gives the card a single
 * tab stop and a single accessible name, instead of the three separate links
 * to the same page a card like this usually accumulates.
 *
 * Both lines are clamped: the title to two lines, the subtitle to one. A long
 * name like "Mercedes-Benz C200 2018" therefore reserves exactly as much room
 * as a short one, which is what keeps every card in a row the same height.
 */
export function VehicleCardHeading({ title, subtitle, to, headingLevel }: Props) {
  return (
    <Box>
      <Typography
        variant="h6"
        component={headingLevel}
        sx={{ lineHeight: 1.3, minHeight: '2.6em', m: 0 }}
      >
        <Box
          component={RouterLink}
          prefetch="intent"
          to={to}
          sx={{
            color: 'inherit',
            textDecoration: 'none',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            transition: 'color 200ms ease',
            '&:hover': { color: 'primary.main' },
            // The stretched hit area. `position: relative` on the card scopes it.
            '&::after': { content: '""', position: 'absolute', inset: 0, zIndex: 1 },
            '&:focus-visible': { outline: 'none' },
            '&:focus-visible::after': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
              borderRadius: 'inherit',
            },
          }}
        >
          {title}
        </Box>
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 1 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}
